package dev.lingvo.shared.screenshots.templates

import dev.lingvo.shared.TextDirection
import dev.lingvo.shared.screenshots.color.guardContrast
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.render.CanvasAlign
import dev.lingvo.shared.screenshots.render.Paths
import dev.lingvo.shared.screenshots.render.ResolvedFont
import dev.lingvo.shared.screenshots.render.ResolvedPaint
import dev.lingvo.shared.screenshots.render.Surface
import dev.lingvo.shared.screenshots.render.resolveColor

/** Shapes and plain text provide explicitly labelled sample-app previews, never substitute customer captures. */
object SampleScreen {
    /** Paints five distinct lived-in screens without image loading, platform APIs or trademark assets. */
    fun paint(surface: Surface, width: Int, height: Int, palette: Palette, slotKey: String) {
        require(width > 0 && height > 0 && SampleApp.slots.any { it.slotKey == slotKey }) { "Unknown sample screen or invalid size" }
        val w = width.toDouble()
        val h = height.toDouble()
        val cardInk = guardContrast(palette.ink, listOf(palette.surface), 4.5)
        fun color(ref: String): ResolvedPaint = ResolvedPaint.Solid(resolveColor(ref, palette, 0))
        fun rect(x: Double, y: Double, rw: Double, rh: Double, ref: String) = surface.fill(Paths.rect(Rect(x * w, y * h, rw * w, rh * h)), color(ref))
        fun text(value: String, x: Double, y: Double, size: Double = .040, ref: String = "@ink", weight: Int = 400) {
            surface.fillText(value, x * w, y * h, ResolvedFont("$weight ${size * w}px Inter, sans-serif", size * w, listOf("Inter"), weight, false),
                color(ref), CanvasAlign.LEFT, TextDirection.LTR, 0.0)
        }
        fun row(y: Double, title: String, detail: String, amount: String) {
            rect(.07, y, .86, .10, "@surface")
            rect(.10, y + .025, .012, .05, "@accent")
            text(title, .14, y + .038, .037, cardInk, 600)
            text(detail, .14, y + .075, .029, cardInk)
            text(amount, .70, y + .055, .034, cardInk, 600)
        }
        surface.save()
        try {
            surface.clip(Paths.rect(Rect(0.0, 0.0, w, h)))
            rect(0.0, 0.0, 1.0, 1.0, "@bg")
            text("9:41", .07, .035, .028, weight = 600)
            text("Tally", .07, .10, .060, weight = 700)
            text("Sample app", .07, .14, .028, "@inkMuted")
            when (slotKey) {
                "home" -> {
                    text("Your subscriptions", .07, .21, .046, weight = 600)
                    text("Monthly total", .07, .26, .032, "@inkMuted")
                    text("42.00", .07, .33, .11, weight = 600)
                    row(.39, "Music", "Renews 12 Nov", "9.00")
                    row(.51, "Cloud storage", "Renews 18 Nov", "6.00")
                    row(.63, "Reading", "Renews 22 Nov", "12.00")
                    row(.75, "Fitness", "Renews 28 Nov", "15.00")
                }
                "add" -> {
                    text("New subscription", .07, .23, .052, weight = 600)
                    row(.30, "Name", "Reading", "")
                    row(.43, "Amount", "Monthly", "12.00")
                    row(.56, "Next renewal", "22 November", "")
                    row(.69, "Reminder", "Two days before", "On")
                    rect(.07, .83, .86, .065, "@accent")
                    text("Save subscription", .14, .872, .040, guardContrast(palette.accentInk, listOf(palette.accent), 4.5), 600)
                }
                "renewals" -> {
                    text("Coming up", .07, .24, .060, weight = 600)
                    text("This month", .07, .30, .034, "@inkMuted")
                    row(.35, "Music", "12 November", "9.00")
                    row(.48, "Cloud storage", "18 November", "6.00")
                    row(.61, "Reading", "22 November", "12.00")
                    row(.74, "Fitness", "28 November", "15.00")
                }
                "insights" -> {
                    text("Where it goes", .07, .24, .056, weight = 600)
                    text("Monthly spending", .07, .30, .034, "@inkMuted")
                    listOf(.24, .39, .30, .45, .34, .28).forEachIndexed { i, bar -> rect(.10 + i * .14, .78 - bar, .085, bar, "@accent") }
                    text("Jun   Jul   Aug   Sep   Oct   Nov", .10, .83, .028)
                }
                "settings" -> {
                    text("Your preferences", .07, .24, .052, weight = 600)
                    row(.31, "Reminders", "Before renewals", "On")
                    row(.44, "Time", "Morning reminders", "09:00")
                    row(.57, "Currency", "Display amounts", "USD")
                    row(.70, "Appearance", "Follow your device", "Auto")
                }
            }
        } finally { surface.restore() }
    }
}
